import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Trash2, Search, Pin } from 'lucide-react'
import { api } from '../services/api'
import Input from './Input'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useLanguageStore } from '../stores/languageStore'

export default function BlockManager({
  entityId,
  entityType,
  blockField,
  viewMode = 'draft',
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()
  const localBlocks = usePageBuilderStore((state) => state.localBlocks)
  const addLocalBlock = usePageBuilderStore((state) => state.addLocalBlock)
  const removeLocalBlock = usePageBuilderStore(
    (state) => state.removeLocalBlock,
  )
  const { currentLanguageCode: locale } = useLanguageStore()

  const resolvedBlockField = blockField || `${entityType}_id`
  const isNew = entityId === 'new'

  const { data: blocksRes, isLoading: blocksLoading } = useQuery({
    queryKey: [
      'blocks',
      { [resolvedBlockField]: entityId, view_mode: viewMode, locale },
    ],
    queryFn: () =>
      api
        .get(`/blocks`, {
          params: {
            [resolvedBlockField]: entityId,
            per_page: 100,
            view_mode: viewMode,
            locale: locale,
          },
        })
        .then((r) => r.data),
    enabled: !!entityId && !!locale && !isNew,
  })

  const { data: blockTypesRes } = useQuery({
    queryKey: ['block-types', 'all'],
    queryFn: () =>
      api
        .get(`/block-types`, { params: { per_page: 100 } })
        .then((r) => r.data),
  })

  const { data: presetsRes } = useQuery({
    queryKey: ['block-presets', locale],
    queryFn: () =>
      api
        .get(`/block-presets`, {
          params: { per_page: 200, locale: locale, is_active: true },
        })
        .then((r) => r.data),
    enabled: !!locale,
  })

  const blocks = blocksRes?.data || []
  const blockTypes = blockTypesRes?.data || []
  const presets = presetsRes?.data || []

  const fixedBlocksForNew = useMemo(() => {
    if (!isNew) return []
    return blockTypes
      .filter((bt) => {
        if (!bt.is_fixed || !bt.is_active) return false
        const entityTypes = bt.fixed_entity_types || []
        if (entityTypes.length === 0) return true
        return entityTypes.includes(entityType)
      })
      .map((bt, index) => ({
        id: `fixed-${bt.id}`,
        block_type_id: bt.id,
        name: bt.name,
        content: {},
        settings: bt.default_settings || {},
        sort_order: 1000 + index,
        is_active: true,
        is_fixed: true,
        block_type: bt,
      }))
  }, [isNew, blockTypes, entityType])

  const allBlocks = isNew
    ? [...localBlocks, ...fixedBlocksForNew]
    : [...blocks, ...localBlocks]
  const regularBlocks = allBlocks.filter((b) => !b.is_fixed)
  const nextSortOrder =
    Math.max(...regularBlocks.map((b) => b.sort_order || 0), 0) + 1

  const filteredBlockTypes = useMemo(() => {
    let filtered = blockTypes

    filtered = filtered.filter((type) => !type.is_fixed)

    if (entityType) {
      filtered = filtered.filter((type) => {
        const entityTypeForBlock = type.entity_type || 'both'
        return (
          entityTypeForBlock === entityType || entityTypeForBlock === 'both'
        )
      })
    }

    if (!searchQuery) return filtered
    return filtered.filter((type) => {
      const translation = type.translations?.find((t) => t.locale === locale)
      const name = translation?.name || type.name || type.slug || ''
      return name.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [blockTypes, searchQuery, entityType, locale])

  const filteredPresets = useMemo(() => {
    const allowedBlockTypeIds = new Set(filteredBlockTypes.map((type) => type.id))

    return presets.filter((preset) => {
      if (!allowedBlockTypeIds.has(preset.block_type_id)) return false
      if (!searchQuery) return true
      const haystack = `${preset.name || ''} ${preset.block_type?.name || ''}`.toLowerCase()
      return haystack.includes(searchQuery.toLowerCase())
    })
  }, [presets, filteredBlockTypes, searchQuery])

  const deleteBlock = useMutation({
    mutationFn: (blockId) =>
      api.delete(`/blocks/${blockId}`, {
        params: { locale: locale },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blocks'],
      })
    },
  })

  const handleAddBlock = (blockTypeId) => {
    const blockType = blockTypes.find((type) => type.id === blockTypeId)
    if (blockType) {
      const translation = blockType.translations?.find(
        (t) => t.locale === locale,
      )
      const blockTypeName =
        translation?.name || blockType.name || blockType.slug || 'Block'
      const existingCount = allBlocks.filter(
        (b) => b.block_type_id === blockTypeId,
      ).length
      const newBlock = {
        id: `local-${Date.now()}-${Math.random()}`,
        [resolvedBlockField]: null,
        block_type_id: blockTypeId,
        name:
          existingCount > 0
            ? `${blockTypeName} ${existingCount + 1}`
            : blockTypeName,
        content: {},
        settings: {},
        sort_order: nextSortOrder,
        is_active: true,
        locale: locale,
        block_type: blockType,
        is_local: true,
      }
      addLocalBlock(newBlock)
      setSearchQuery('')
    }
  }

  const handleAddPreset = async (preset) => {
    const blockType = blockTypes.find((type) => type.id === preset.block_type_id)
    if (!blockType) return

    if (isNew) {
      addLocalBlock({
        id: `local-preset-${Date.now()}-${Math.random()}`,
        [resolvedBlockField]: null,
        block_type_id: preset.block_type_id,
        preset_id: preset.id,
        is_preset_synced: true,
        name: preset.name,
        content: preset.content || {},
        settings: preset.settings || {},
        sort_order: nextSortOrder,
        is_active: true,
        locale: locale,
        block_type: blockType,
        preset: { id: preset.id, name: preset.name },
        is_local: true,
      })
      setSearchQuery('')
      return
    }

    await api.post('/blocks/create-from-preset', {
      [resolvedBlockField]: Number(entityId),
      preset_id: Number(preset.id),
      sort_order: nextSortOrder,
      locale: locale,
    })

    queryClient.invalidateQueries({
      queryKey: ['blocks'],
    })
    setSearchQuery('')
  }

  const handleDeleteBlock = (blockId) => {
    const blockIdStr = String(blockId)
    if (blockIdStr.startsWith('local-')) {
      removeLocalBlock(blockId)
    } else {
      if (confirm('Delete this block?')) {
        deleteBlock.mutate(blockId)
      }
    }
  }

  const handleBlockClick = (block) => {
    const setSelectedBlockId = usePageBuilderStore.getState().setSelectedBlockId
    setSelectedBlockId(block.id)
  }

  const handleDuplicate = (blockId) => {
    const blockToDuplicate = allBlocks.find((block) => block.id === blockId)
    if (!blockToDuplicate) return

    const duplicatedBlock = {
      ...blockToDuplicate,
      id: `local-${Date.now()}-${Math.random()}`,
      name: `${blockToDuplicate.name} (Copy)`,
      sort_order: nextSortOrder,
      is_local: true,
      locale: locale,
    }

    addLocalBlock(duplicatedBlock)
  }

  return (
    <div className="flex-1 px-2 py-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Blocks</h3>
      </div>

      <div className="space-y-2 p-2 bg-gray-50 rounded-lg mb-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
            <Search className="h-3 w-3 text-gray-400" />
          </div>
          <Input
            placeholder="Search block types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="sm"
            className="pl-7"
          />
        </div>
        <div className="max-h-32 overflow-y-auto space-y-1">
          {filteredBlockTypes.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-2">
              No block types found
            </div>
          ) : (
            filteredBlockTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => handleAddBlock(type.id)}
                className="w-full text-left px-2 py-1 rounded text-xs hover:bg-gray-100 text-gray-700"
              >
                {type.name || type.slug}
              </button>
            ))
          )}
        </div>
        <div className="max-h-28 overflow-y-auto space-y-1 border-t border-gray-200 pt-2">
          {filteredPresets.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-1">
              No presets found
            </div>
          ) : (
            filteredPresets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleAddPreset(preset)}
                className="w-full text-left px-2 py-1 rounded text-xs hover:bg-gray-100 text-indigo-700"
              >
                {preset.name}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-1">
        {blocksLoading ? (
          <div className="text-xs text-gray-500 text-center py-2">
            Loading blocks...
          </div>
        ) : allBlocks.length === 0 ? (
          <div className="text-xs text-gray-500 text-center py-2">
            No blocks yet. Click on a block type above to add one.
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-400 mb-2">
              {allBlocks.length} block{allBlocks.length !== 1 ? 's' : ''} on
              this {entityType}
            </div>
            {allBlocks
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map((block, index) => (
                <div
                  key={block.id}
                  className={`flex items-center justify-between p-2 border rounded text-xs hover:bg-gray-50 cursor-pointer ${
                    block.is_fixed
                      ? 'bg-amber-50 border-amber-200'
                      : block.is_local
                        ? 'bg-blue-50 border-blue-200'
                        : 'border-gray-200'
                  }`}
                  onClick={() => handleBlockClick(block)}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-gray-400 font-mono text-xs">
                      #{index + 1}
                    </span>

                    <span className="text-gray-700">
                      {block.block_type?.name || 'Unknown'}
                    </span>

                    {block.is_fixed && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                        <Pin className="h-2.5 w-2.5" />
                        Fixed
                      </span>
                    )}
                    {block.is_local && (
                      <span className="px-1 py-0.5 text-xs rounded bg-blue-100 text-blue-600">
                        New
                      </span>
                    )}
                    {block.is_preset_synced && (
                      <span className="px-1 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700">
                        Preset
                      </span>
                    )}
                  </div>
                  {!block.is_fixed && (
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDuplicate(block.id)
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="Duplicate"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteBlock(block.id)
                        }}
                        className="text-red-400 hover:text-red-600 p-1"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  )
}
